import React, { useState } from 'react';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import {
  FEEDBACK_ATTACHMENT_MAX_COUNT,
  type FeedbackAttachmentDraft,
  formatFeedbackAttachmentSize,
  isFeedbackAttachmentMimeType,
  validateFeedbackAttachmentDrafts,
} from '@shared/feedbackAttachments';
import { showError } from '../../../shared/ui/appToast';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  value: FeedbackAttachmentDraft[];
  onChange: (files: FeedbackAttachmentDraft[]) => void;
  disabled?: boolean;
};

export function SupportAttachmentPicker({ value, onChange, disabled = false }: Props) {
  const [busy, setBusy] = useState(false);

  const addDraft = (draft: FeedbackAttachmentDraft) => {
    const next = [...value, draft];
    const error = validateFeedbackAttachmentDrafts(next);
    if (error) {
      showError('No se pudo adjuntar', error);
      return;
    }
    onChange(next);
  };

  const pickImage = async () => {
    if (disabled || busy || value.length >= FEEDBACK_ATTACHMENT_MAX_COUNT) return;
    setBusy(true);
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.9,
        allowsEditing: false,
      });
      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        const mimeType = asset.mimeType ?? 'image/jpeg';
        if (!isFeedbackAttachmentMimeType(mimeType)) {
          throw new Error('Solo podés adjuntar JPG, PNG, WebP o HEIC.');
        }
        addDraft({
          uri: asset.uri,
          name: asset.fileName ?? 'imagen.jpg',
          mimeType,
          size: asset.fileSize ?? 1,
        });
      }
    } catch (error) {
      showError('No se pudo adjuntar la imagen', error instanceof Error ? error.message : 'Probá de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const captureImage = async () => {
    if (disabled || busy || value.length >= FEEDBACK_ATTACHMENT_MAX_COUNT) return;
    setBusy(true);
    try {
      const result = await ImagePicker.launchCameraAsync({ quality: 0.9, allowsEditing: false });
      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        const mimeType = asset.mimeType ?? 'image/jpeg';
        if (!isFeedbackAttachmentMimeType(mimeType)) throw new Error('Solo podés adjuntar imágenes.');
        addDraft({ uri: asset.uri, name: asset.fileName ?? 'foto.jpg', mimeType, size: asset.fileSize ?? 1 });
      }
    } catch (error) {
      showError('No se pudo sacar la foto', error instanceof Error ? error.message : 'Probá de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async () => {
    if (disabled || busy || value.length >= FEEDBACK_ATTACHMENT_MAX_COUNT) return;
    setBusy(true);
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf'],
        multiple: false,
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        const mimeType = asset.mimeType ?? 'application/pdf';
        if (!isFeedbackAttachmentMimeType(mimeType)) {
          throw new Error('Solo podés adjuntar imágenes o PDF.');
        }
        addDraft({
          uri: asset.uri,
          name: asset.name,
          mimeType,
          size: asset.size ?? 1,
        });
      }
    } catch (error) {
      showError('No se pudo adjuntar el archivo', error instanceof Error ? error.message : 'Probá de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>Adjuntos opcionales</Text>
      <Text style={styles.hint}>Sumá fotos, capturas o PDF para ayudarnos a entenderlo.</Text>
      <View style={styles.actions}>
        <TouchableOpacity style={styles.action} onPress={() => void captureImage()} disabled={disabled || busy}>
          <Text style={styles.actionText}>Sacar foto</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.action} onPress={() => void pickImage()} disabled={disabled || busy}>
          <Text style={styles.actionText}>Galería</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.action} onPress={() => void pickFile()} disabled={disabled || busy}>
          <Text style={styles.actionText}>Elegir archivo</Text>
        </TouchableOpacity>
      </View>
      {value.map((file, index) => (
        <View key={`${file.uri}-${index}`} style={styles.fileRow}>
          {file.mimeType.startsWith('image/') ? <Image source={{ uri: file.uri }} style={styles.thumbnail} /> : <View style={styles.pdfBadge}><Text style={styles.pdfText}>PDF</Text></View>}
          <View style={styles.fileInfo}>
            <Text numberOfLines={1} style={styles.fileName}>{file.name}</Text>
            <Text style={styles.fileSize}>{formatFeedbackAttachmentSize(file.size)}</Text>
          </View>
          <TouchableOpacity onPress={() => onChange(value.filter((_, i) => i !== index))} disabled={disabled} accessibilityLabel={`Quitar ${file.name}`}>
            <Text style={styles.remove}>×</Text>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.xs },
  label: { fontSize: 13, fontWeight: '600', color: ticketColors.ink2 },
  hint: { fontSize: 12, lineHeight: 17, color: ticketColors.ink3 },
  actions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
  action: { minHeight: 40, justifyContent: 'center', paddingHorizontal: spacing.md, borderRadius: 9, borderWidth: 1, borderColor: ticketColors.ruleSoft, backgroundColor: ticketColors.paper2 },
  actionText: { fontSize: 13, fontWeight: '600', color: ticketColors.brandInk },
  fileRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: 9, borderWidth: 1, borderColor: ticketColors.ruleSoft, backgroundColor: '#FFFFFF' },
  thumbnail: { width: 40, height: 40, borderRadius: 6, backgroundColor: ticketColors.brandSoft },
  pdfBadge: { width: 40, height: 40, borderRadius: 6, alignItems: 'center', justifyContent: 'center', backgroundColor: ticketColors.brandSoft },
  pdfText: { fontSize: 10, fontWeight: '800', color: ticketColors.brandInk },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { fontSize: 13, color: ticketColors.ink, fontWeight: '600' },
  fileSize: { fontSize: 11, color: ticketColors.ink3, marginTop: 2 },
  remove: { fontSize: 24, lineHeight: 24, color: ticketColors.ink3, paddingHorizontal: spacing.xs },
});
